import dayjs, { type Dayjs } from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"
import timezonePlugin from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"

dayjs.extend(utc)
dayjs.extend(timezonePlugin)
dayjs.extend(customParseFormat)

// The utc plugin's types leave out the locale argument customParseFormat reads.
const parseUtc = dayjs.utc as unknown as (
  value: string,
  format: string,
  locale: string,
  strict: boolean
) => Dayjs

const RENDERED_TIMES = ["HH:mm", "hh:mm A"]

export interface FileDateAccount {
  dateFormat: string
  timezone: string
}

const inZone = (wallClock: string, timezone: string): number | null => {
  try {
    return dayjs.tz(wallClock, timezone).valueOf()
  } catch {
    // The backend accepts zone names this browser may not know, such as `localtime`.
    return null
  }
}

/** The instant behind a Files date, or null for a shape the API does not send.
 * An album file's date arrives rendered in the account's format and timezone, to
 * the minute, with English month names and AM/PM; a document's is UTC with no zone. */
export const readFileDate = (
  value: string,
  { dateFormat, timezone }: FileDateAccount
): number | null => {
  // One format at a time and in UTC: a format array parses in the browser's zone,
  // where a wall clock inside its spring-forward hour does not exist.
  for (const time of RENDERED_TIMES) {
    const wallClock = parseUtc(value, `${dateFormat} ${time}`, "en", true)
    if (wallClock.isValid()) return inZone(wallClock.format("YYYY-MM-DDTHH:mm:ss"), timezone)
  }

  const document = dayjs.utc(value, "YYYY-MM-DD HH:mm:ss", true)
  return document.isValid() ? document.valueOf() : null
}

const DAY = "YYYY-MM-DD"

const calendarDayIn = (instant: number, timezone: string): string => {
  try {
    return dayjs(instant).tz(timezone).format(DAY)
  } catch {
    return dayjs.utc(instant).format(DAY)
  }
}

// On the same scale as `readDocumentDay`, so an upload and a paper date compare directly.
export const uploadDayOf = (instant: number, timezone: string): number =>
  dayjs.utc(calendarDayIn(instant, timezone), DAY).valueOf()
